export interface TreemapInput {
    key: string
    value: number
}

export interface TreemapRect {
    key: string
    x: number
    y: number
    w: number
    h: number
}

interface Rect {
    x: number
    y: number
    w: number
    h: number
}

const worst = (row: number[], length: number): number => {
    if (!row.length || length <= 0) return Infinity

    let total = 0
    let max = -Infinity
    let min = Infinity

    for (const area of row) {
        total += area
        if (area > max) max = area
        if (area < min) min = area
    }

    const lengthSquared = length * length
    const totalSquared = total * total

    return Math.max((lengthSquared * max) / totalSquared, totalSquared / (lengthSquared * min))
}

export const squarify = (inputs: TreemapInput[], x: number, y: number, width: number, height: number): TreemapRect[] => {
    const items = inputs.filter(item => item.value > 0).sort((a, b) => b.value - a.value)
    const total = items.reduce((acc, item) => acc + item.value, 0)
    const output: TreemapRect[] = []

    if (!items.length || total <= 0 || width <= 0 || height <= 0) return output

    const scale = (width * height) / total
    const queue = items.map(item => ({ key: item.key, area: item.value * scale }))
    const rect: Rect = { x, y, w: width, h: height }
    let row: Array<{ key: string, area: number }> = []

    const layoutRow = (entries: Array<{ key: string, area: number }>) => {
        const sum = entries.reduce((acc, entry) => acc + entry.area, 0)
        if (sum <= 0) return

        if (rect.w >= rect.h) {
            const columnWidth = sum / rect.h
            let cursor = rect.y

            entries.forEach((entry, index) => {
                const tileHeight = index === entries.length - 1 ? rect.y + rect.h - cursor : entry.area / columnWidth
                output.push({ key: entry.key, x: rect.x, y: cursor, w: columnWidth, h: tileHeight })
                cursor += tileHeight
            })

            rect.x += columnWidth
            rect.w -= columnWidth
        }
        else {
            const rowHeight = sum / rect.w
            let cursor = rect.x

            entries.forEach((entry, index) => {
                const tileWidth = index === entries.length - 1 ? rect.x + rect.w - cursor : entry.area / rowHeight
                output.push({ key: entry.key, x: cursor, y: rect.y, w: tileWidth, h: rowHeight })
                cursor += tileWidth
            })

            rect.y += rowHeight
            rect.h -= rowHeight
        }
    }

    while (queue.length) {
        const shortest = Math.min(rect.w, rect.h)
        const next = queue[0]
        const current = row.map(entry => entry.area)

        if (!row.length || worst([...current, next.area], shortest) <= worst(current, shortest)) {
            row.push(next)
            queue.shift()
        }
        else {
            layoutRow(row)
            row = []
        }
    }

    if (row.length) layoutRow(row)

    return output
}
